import React, { useEffect, useState, useCallback } from "react";
import {
  Plus,
  MagnifyingGlass,
  DotsThreeVertical,
  Power,
  Key,
  PencilSimple,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const CREATE_EMPTY = { name: "", email: "", phone: "", password: "" };
const EDIT_EMPTY = { name: "", phone: "" };

function CreateTeacherDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(CREATE_EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setForm(CREATE_EMPTY);
      setError("");
    }
  }, [open]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/school/teachers", cleanPayload(form));
      toast.success(`Teacher ${data.name} added`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-teacher-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add teacher</DialogTitle>
          <DialogDescription>
            Creates a teacher account in your school. They'll log in with this email and password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name *</Label>
            <Input required value={form.name} onChange={update("name")} className="mt-1.5" data-testid="teacher-name-input" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input required type="email" value={form.email} onChange={update("email")} className="mt-1.5" data-testid="teacher-email-input" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="mt-1.5" placeholder="+91-…" />
          </div>
          <div>
            <Label>Initial password *</Label>
            <Input
              required
              type="text"
              minLength={8}
              value={form.password}
              onChange={update("password")}
              className="mt-1.5 font-mono"
              data-testid="teacher-password-input"
              placeholder="Min 8 characters"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800" data-testid="teacher-submit-btn">
              {submitting ? "Adding…" : "Add teacher"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditTeacherDialog({ open, onOpenChange, teacher, onUpdated }) {
  const [form, setForm] = useState(EDIT_EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open && teacher) {
      setForm({ name: teacher.name || "", phone: teacher.phone || "" });
      setError("");
    }
  }, [open, teacher]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.patch(`/school/teachers/${teacher.id}`, cleanPayload(form));
      toast.success("Teacher updated");
      onUpdated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-teacher-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit teacher</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={update("name")} className="mt-1.5" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="mt-1.5" />
          </div>
          <div className="text-xs text-slate-500">
            Email cannot be changed. Use <em>Reset password</em> from the actions menu to change credentials.
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800">
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onOpenChange, teacher }) {
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setPw("");
      setError("");
    }
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/school/teachers/${teacher.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${teacher.name}`);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reset-teacher-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Reset password</DialogTitle>
          <DialogDescription>
            Set a new password for this teacher. They'll need to use it on their next sign-in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm text-slate-600">
            New password for <strong>{teacher?.name}</strong> ({teacher?.email})
          </div>
          <div>
            <Label>New password *</Label>
            <Input
              required
              type="text"
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="mt-1.5 font-mono"
              data-testid="reset-teacher-password-input"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800" data-testid="reset-teacher-submit-btn">
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async (q = "", st = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (st) params.set("status", st);
      const url = `/school/teachers${params.toString() ? `?${params.toString()}` : ""}`;
      const { data } = await api.get(url);
      setTeachers(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search, statusFilter), 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, load]);

  const toggleStatus = async (t) => {
    try {
      const { data } = await api.post(`/school/teachers/${t.id}/toggle-status`);
      setTeachers((s) => s.map((x) => (x.id === t.id ? data : x)));
      toast.success(`${data.name} is now ${data.is_active ? "active" : "inactive"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            People
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Teachers <span className="text-slate-400 font-normal">({total})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Onboard teachers and manage their access. Each teacher logs in to their own
            classroom workspace.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="add-teacher-btn"
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          <Plus size={16} weight="bold" /> Add teacher
        </Button>
      </div>

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, email, or phone…"
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="teachers-search-input"
            />
          </div>
          <div className="flex gap-1.5">
            {[
              { v: "", label: "All" },
              { v: "active", label: "Active" },
              { v: "inactive", label: "Inactive" },
            ].map((f) => (
              <button
                key={f.label}
                onClick={() => setStatusFilter(f.v)}
                data-testid={`teachers-filter-${f.label.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  statusFilter === f.v
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {error && (
          <div className="px-4 py-3 text-sm text-red-600 bg-red-50 border-b border-red-200">{error}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="teachers-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Teacher</th>
                <th className="text-left font-semibold px-4 py-3">Email</th>
                <th className="text-left font-semibold px-4 py-3">Phone</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && teachers.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : teachers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="teachers-empty">
                    No teachers yet. Click <strong>Add teacher</strong> to onboard one.
                  </td>
                </tr>
              ) : (
                teachers.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50 transition-colors" data-testid={`teacher-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">
                          {(t.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{t.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{t.email}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{t.phone || "—"}</td>
                    <td className="px-4 py-3">
                      {t.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-emerald-100 text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-200 text-slate-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                            data-testid={`teacher-actions-${t.id}`}
                          >
                            <DotsThreeVertical size={18} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditing(t)} data-testid={`edit-teacher-${t.id}`}>
                            <PencilSimple size={14} className="mr-2" /> Edit profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setResetting(t)} data-testid={`reset-teacher-${t.id}`}>
                            <Key size={14} className="mr-2" /> Reset password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(t)} data-testid={`toggle-teacher-${t.id}`}>
                            <Power size={14} className="mr-2" />
                            {t.is_active ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateTeacherDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(t) => {
          setTeachers((s) => [t, ...s]);
          setTotal((n) => n + 1);
        }}
      />
      <EditTeacherDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        teacher={editing}
        onUpdated={(t) => setTeachers((s) => s.map((x) => (x.id === t.id ? t : x)))}
      />
      <ResetPasswordDialog
        open={!!resetting}
        onOpenChange={(v) => !v && setResetting(null)}
        teacher={resetting}
      />
    </div>
  );
}
